import { Cpu } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface InverterGroup {
  key: string;
  displayName: string;
  manufacturer: string;
  model: string | null;
  loggerHost: string;
  currentKw: number | null;
  groupNominalKw: number | null;
  percentOfGroupNominal: number | null;
  effectiveCapKw: number | null;
  percentOfEffectiveCap: number | null;
  expectedInverterCount: number;
  availableInverterCount: number;
  statusRaw: number | null;
  statusTag: string | null;
  qualityFlag: string;
  lastReadAtUtc: string | null;
  notes?: string | null;
}

interface Props {
  group: InverterGroup;
  assetName?: string;
}

const STATUS_COLORS: Record<string, { bg: string; fg: string; ring: string }> = {
  Ok:        { bg: 'rgba(13,122,60,0.10)', fg: 'var(--ok)', ring: 'var(--ok)' },
  Warning:   { bg: 'rgba(245,158,11,0.10)', fg: 'var(--warn)', ring: 'var(--warn)' },
  Fault:     { bg: 'var(--surface-2)', fg: 'var(--accent)', ring: 'var(--accent)' },
  Off:       { bg: 'rgba(107,107,112,0.10)', fg: 'var(--text-muted)', ring: 'var(--text-dim)' },
};

function fmtKw(v: number | null) {
  if (v == null) return '—';
  if (Math.abs(v) >= 1000) return v.toFixed(0);
  if (Math.abs(v) >= 100)  return v.toFixed(1);
  return v.toFixed(2);
}

// Та сама вертикальна шкала, що й «Технічний стан інверторів» на
// /scada/station (InverterStrip.tsx) — тут на токенах var(--...) кабінету
// замість scada.*, бо ця сторінка сама підтримує світлу/темну тему, а
// SCADA-токени зав'язані на завжди-темний дашборд диспетчера.
export function VerticalScale({ value, max, color = 'var(--ok)' }: { value: number | null; max: number; color?: string }) {
  const frac = value == null || max <= 0 ? 0 : Math.max(0, Math.min(1, value / max));
  const N = 24;
  // Прямокутний трикутник із поділок: вертикальний катет — ліва вісь, вістря
  // внизу, основа вгорі. Довжина поділки росте лінійно з висотою, тож площа
  // підсвіченої частини сама показує продуктивність інвертора — око ловить
  // різницю ще до того, як прочитає «% номіналу».
  const W_MIN = 2, W_MAX = 16;
  const wAt = (f: number) => W_MIN + f * (W_MAX - W_MIN);
  const ticks: any[] = [];
  for (let i = 0; i <= N; i++) {
    const f = i / N;
    const major = i % 6 === 0;
    const lit = f <= frac + 1e-6;
    ticks.push(<div key={i} style={{
      position: 'absolute', bottom: `${f * 100}%`, left: 0,
      width: wAt(f) + (major ? 2 : 0), height: major ? 1.6 : 1,
      background: lit ? color : 'var(--line-strong)', opacity: lit ? 0.95 : 0.4,
      transform: 'translateY(50%)', borderRadius: 1,
      transition: 'background .5s, opacity .5s',
    }} />);
  }
  return (
    <div style={{ position: 'relative', width: W_MAX + 6, height: 58, flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 1, background: 'var(--line-strong)', opacity: 0.4 }} />
      {ticks}
      {value != null && <div style={{
        position: 'absolute', bottom: `${frac * 100}%`, left: -2, transform: 'translateY(50%)',
        width: wAt(frac) + 5, height: 2.4, background: color, borderRadius: 2,
        boxShadow: `0 0 6px ${color}`, transition: 'bottom .6s cubic-bezier(.4,0,.2,1), width .6s cubic-bezier(.4,0,.2,1)',
      }} />}
    </div>
  );
}

/**
 * Картка групи інверторів — той самий каркас, що й картка одного інвертора
 * на «Технічний стан інверторів» (/scada/station): чіп-іконка + назва в
 * шапці, статус праворуч, велике число + % номіналу зліва, вертикальна
 * шкала справа. Дані — реальна телеметрія з бекенду
 * (GET /forecasting/assets/:id/inverters/latest → data.groups), той самий
 * запит, що живить решту InverterTelemetryWidget; тут нічого не мокається.
 */
export default function InverterGroupCard({ group, assetName }: Props) {
  const { t } = useTranslation();
  const statusKey = group.statusTag && STATUS_COLORS[group.statusTag] ? group.statusTag : 'Off';
  const status = STATUS_COLORS[statusKey];
  const pct = Math.max(0, Math.min(100, group.percentOfGroupNominal ?? 0));

  const sectorIdx = (() => {
    const m = group.key.match(/_(\d+)$/);
    return m ? m[1] : '';
  })();
  const title = assetName
    ? `${assetName.replace('СЕС ', '')} ${group.manufacturer}${sectorIdx ? ' ' + sectorIdx : ''}`.trim()
    : group.displayName;

  const isCurtailed = group.effectiveCapKw != null && group.groupNominalKw != null
    && group.effectiveCapKw < group.groupNominalKw;
  const curtailRatio = isCurtailed && group.groupNominalKw
    ? Math.round((group.effectiveCapKw! / group.groupNominalKw) * 100)
    : null;

  return (
    <div className="tile p-3.5 flex flex-col" style={{ gap: 11 }}>
      {/* Дзеркальна шапка: статус зліва, назва групи праворуч. */}
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center shrink-0" style={{ gap: 5, fontSize: 10.5, color: status.fg }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: status.fg, display: 'inline-block' }} />
          {group.statusTag ?? '—'}
        </span>
        <span className="inline-flex items-center min-w-0" style={{ gap: 8 }}>
          <span style={{ width: 27, height: 27, borderRadius: 8, background: 'var(--surface-2)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Cpu size={15} strokeWidth={1.9} style={{ color: 'var(--text)' }} />
          </span>
          <span className="font-display truncate" style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }} title={title}>{title}</span>
        </span>
      </div>

      {isCurtailed && curtailRatio != null && (
        <span
          className="text-[10px] font-semibold px-1.5 py-0.5 rounded self-start"
          style={{ background: 'color-mix(in srgb, var(--warn) 14%, transparent)', color: 'var(--warn)' }}
          title={`Curtailed to ${curtailRatio}% — effective cap ${group.effectiveCapKw?.toFixed(0)} kW`}
        >
          {curtailRatio}% cap
        </span>
      )}

      {/* Шкала зліва, одразу за нею значення, підписи — при правому краї. */}
      <div className="flex items-center" style={{ gap: 10 }}>
        <VerticalScale value={pct} max={100} color={status.ring} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-baseline justify-between">
            <span className="font-display" style={{ fontSize: 19, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>
              {fmtKw(group.currentKw)}<span style={{ fontSize: 10, color: 'var(--text-dim)', fontWeight: 400 }}> кВт</span>
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{t('inv_tel_power')}</span>
          </div>
          <div className="flex items-baseline justify-between" style={{ marginTop: 5 }}>
            <span className="font-display" style={{ fontSize: 13, fontWeight: 600, color: status.fg }}>
              {group.percentOfGroupNominal != null ? `${group.percentOfGroupNominal}%` : '—'}
            </span>
            <span style={{ fontSize: 10, color: 'var(--text-dim)' }}>{t('inv_tel_pct_nominal')}</span>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid var(--line)' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t('inv_tel_in_operation')}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{group.availableInverterCount}/{group.expectedInverterCount}</span>
      </div>
    </div>
  );
}
